/**
 * tm/pages/MobileWork.tsx — 지도사 휴대폰 처리 화면 /m (2026-10-12 지도사 「모바일에는 내가 꼭 쓰는 기능만」)
 *  1. 상담 · 통화 메모 남기기(FP 요청 포함 · FP 공개 · 고객 공개)
 *  2. FP 밑에 업체 등록(간이 — WM 은 FP 의 WM 자동)
 *  3. FP 등록(간이)
 *  4. 업체 밑에 업무 등록(템플릿 · 첫 단계 목표일 · 누구 차례)
 *  5. 담당자에게 업무 요청(일일 업무 · 담당자 지정)
 *  0. 오늘 할 일(내 일일 업무 — 글 · 캡처로 바로 등록 · 체크해서 끝 · MobileTodo.tsx · 2026-10-12 · 「말로」는 인식이 잘 안 돼 없앰)
 *  2026-10-12 밤 첫 화면 다시 짬(UX 패널 「안 읽고 누르기」 4/10 → 확정안 C): 찾기 칸 · 오늘 만날 분 칩 · 다음 일정 카드 · 색 다른 2×2 타일 · 등록 3칸
 *   - 찾기 · 업체 한눈에 · 팀장님 한눈에 · 연락처 내려받기 = MobileFind.tsx / 상담 · 강의 일정 · 등록 = MobileSched.tsx / 공용 조각 = mobileKit.tsx
 *   - 타일 자리 고정(근육 기억) · 빨강은 배지에만 · 타일에 설명 문장 없음 · 스크롤 없이 한 화면
 *  로그인 그대로 · 큰 글씨 · 한 손. 저장 뒤 「이어서 할 일」로 바로 넘어간다(메모 → 업무 등록 · 업체 등록 → 업무 등록 …).
 *  2026-10-12 UI/UX 검수 반영: 저장 버튼 아래 고정 · 덜 채우면 회색(누르면 빠진 칸 안내) · 연타 막기 · 휴대폰 뒤로 가기 = 앞 화면 ·
 *   저장 뒤 「이어서」로 바로 내려감 · 고객 공개 미리보기 · 통화로 회신 받음 · 날짜 칩 · PC 와 같은 입력 강제(WM · 누구 차례 기본값 없음)
 */
import CollectLinkPicker from '../components/CollectLinkPicker';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTMStore } from '../hooks';
import * as api from '../api';
import * as todosApi from '../api-todos';
import type { Staff } from '../components/AssigneeChipPicker';
import type { Company, FP, Task, TaskStep, TaskTemplate } from '../types';
import { ceoShown } from '../types';
import { AppBar, G, Icon, ListRow, TEAL, Top, clamp, usePortalStyles, useToast } from '../portal-ui/tds';
import { addDays, companyLine, curStep, diffDays, kstToday, md, stepEx } from '../flow/flowLogic';
import { short } from '../portal-ui/portalLogic';
import MobileTodo, { shrink } from './MobileTodo';
import MobileLoan from './MobileLoan';
import MobileCall from './MobileCall';
import MobileNote from './MobileNote';
import { BOARD_TABS, BoardView, CompanyView, ContactsView, FindBar, FindView, FpView, PickChips, boardRows, quickPicks, remember } from './MobileFind';
import type { BoardTab } from './MobileFind';
import type { Hit } from './MobileFind';
import { ConsultNewForm, LectureNewForm, SchedView, nextUp, schedItems, whenText } from './MobileSched';
import { getLectures } from '../api-lectures';
import type { Lecture } from '../api-lectures';
import { Button, Card, CardHead, Check, Chips, DatePick, Field, NAVY, Next, Picker, SaveBar, TOSS, Tint, guard, input, label } from './mobileKit';
import type { ByRecent } from './mobileKit';

type View = 'home' | 'todo' | 'memo' | 'company' | 'fp' | 'task' | 'request' | 'find' | 'co' | 'fpv' | 'sched' | 'consultNew' | 'lectureNew' | 'contacts' | 'board' | 'loan' | 'call' | 'note';
const DEEP: View[] = ['todo', 'find', 'sched', 'loan', 'call'];   // 홈 화면 바로가기 /m?v=find · /m?v=sched · /m?v=todo · /m?v=loan(대출 링크 보내기) · /m?v=call(통화 녹음 · 휴대폰 「공유」는 &shared=1)
interface Me { id: string; name: string }
interface Pre { companyId?: string; fpId?: string; text?: string; back?: 'company' | 'memo'; tab?: BoardTab; shared?: boolean }

export default function MobileWork({ me }: { me: Me | null }) {
  usePortalStyles();
  const store = useTMStore();
  // 바로가기 /m?v=find(전화 올 때) · ?v=todo · ?v=sched · 새로 불러와도(휴대폰이 탭을 다시 불러옴) 보던 화면 · 고른 업체 그대로
  const st0 = (typeof window !== 'undefined' ? window.history.state : null) as { m?: View; p?: Pre } | null;
  // 연락처의 주소 /m?co=<업체> · /m?fp=<팀장님> = 그 한눈에로 바로(연락처 내려받기 · 2026-10-12 패널)
  const qs0 = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const [view, setView] = useState<View>(() => {
    if (st0 && st0.m) return st0.m;
    if (qs0.get('co')) return 'co';
    if (qs0.get('fp')) return 'fpv';
    const q = qs0.get('v');
    return q && (DEEP as string[]).includes(q) ? (q as View) : 'home';
  });
  const [todoCount, setTodoCount] = useState<{ left: number; old: number } | null>(null);
  const [pre, setPre] = useState<Pre>(() => (st0 && st0.p ? st0.p : qs0.get('co') ? { companyId: qs0.get('co') || '' } : qs0.get('fp') ? { fpId: qs0.get('fp') || '' } : qs0.get('shared') ? { shared: true } : {}));
  const [toastNode, toast] = useToast(100);   // 아래 고정 저장 버튼 위로
  const [staff, setStaff] = useState<Staff[] | null>(null);   // null = 불러오는 중
  const loadStaff = () => { setStaff(null); todosApi.getStaff().then(r => setStaff(r.data || [])).catch(() => setStaff([])); };
  useEffect(() => { document.title = '업무 처리'; loadStaff(); }, []);
  // 휴대폰 「공유 › 로움」(2026-10-28 통화 녹음) — 홈 화면에 추가하면 공유 목록에 뜬다 · 서비스 워커(public/m-sw.js)는 /m/share 로 온 파일만 받아 둔다
  useEffect(() => {
    if (!document.querySelector('link[rel="manifest"]')) { const l = document.createElement('link'); l.rel = 'manifest'; l.href = '/m-manifest.json'; document.head.appendChild(l); }
    if ('serviceWorker' in navigator) navigator.serviceWorker.register('/m-sw.js', { scope: '/m/' }).catch(() => undefined);
  }, []);
  // 처음 화면 「오늘 할 일」 칸에 남은 수 — 열기 전에도 떠오르게
  useEffect(() => {
    if (view !== 'home') return;
    const t0 = kstToday();
    todosApi.getPendingForMe().then(r => {
      const mine = (r.data || []).filter(t => (!me || (Array.isArray(t.assignees) && t.assignees.includes(me.id))) && (!t.due_date || String(t.due_date).slice(0, 10) <= t0));
      const old = mine.filter(t => { const ms = new Date(t.created_at).getTime(); return !isNaN(ms) && new Date(ms + 9 * 3600 * 1000).toISOString().slice(0, 10) < t0; }).length;
      setTodoCount({ left: mine.length, old });
    }).catch(() => setTodoCount(null));
  }, [view, me]);
  useEffect(() => { window.scrollTo(0, 0); }, [view]);
  // 휴대폰 뒤로 가기 = 앞 화면(예전엔 /m 밖으로 나가 적던 글이 사라짐)
  const depth = useRef(0);   // /m 안에서 쌓은 기록 수
  useEffect(() => {
    const on = (e: PopStateEvent) => { const st = e.state as { m?: View; p?: Pre } | null; depth.current = Math.max(0, depth.current - 1); setPre(st && st.p ? st.p : {}); setView(st && st.m ? st.m : 'home'); };
    window.addEventListener('popstate', on);
    return () => window.removeEventListener('popstate', on);
  }, []);

  const go = (v: View, p: Pre = {}) => {
    setPre(p);
    if (v === 'home') {
      // 쌓은 만큼 한 번에 되돌려 처음 화면 기록으로(그 뒤 뒤로 가기 = /m 밖)
      const n = depth.current;
      if (n > 0) { depth.current = 1; window.history.go(-n); } else setView('home');
      return;
    }
    try { window.history.pushState({ m: v, p }, ''); depth.current += 1; } catch { /* 기록 실패해도 화면은 넘어감 */ }
    setView(v);
  };
  // 강의 일정(앞으로) — 첫 화면 「다음 일정」 · 일정 화면 · 겹침 검사
  const [lectures, setLectures] = useState<Lecture[] | null>(null);
  const loadLectures = () => getLectures({ period: 'upcoming' }).then(r => setLectures(r.data || [])).catch(() => setLectures(l => l || []));
  useEffect(() => { void loadLectures(); }, []);
  useEffect(() => { if (view === 'home' || view === 'sched') { void store.refreshConsultations(); if (view === 'sched') void loadLectures(); } }, [view]);   // eslint-disable-line react-hooks/exhaustive-deps
  const open = (h: Hit) => { remember(h.kind, h.id); go(h.kind === 'co' ? 'co' : 'fpv', h.kind === 'co' ? { companyId: h.id } : { fpId: h.id }); };
  const openCo = (id: string) => open({ kind: 'co', id, name: '', sub: '' });
  const openFp = (id: string) => open({ kind: 'fp', id, name: '', sub: '' });
  const saveFpMemo = async (f: FP, text: string): Promise<boolean> => {
    try {
      const memo = `[${md(kstToday())}] ${text}${f.memo ? `\n${f.memo}` : ''}`.slice(0, 4000);
      const r = await api.updateFp(f.id, { memo });
      if (!r.success) throw new Error(r.error || '');
      await store.refresh();
      toast('팀장님 메모를 남겼어요');
      return true;
    } catch { toast('저장하지 못했어요 · 다시 눌러 주세요'); return false; }
  };
  const fpOf = (c: Company): FP | undefined => store.fps.find(f => (c.fps || []).some(cf => cf.fp_id === f.id));
  const compSub = (c: Company) => [fpOf(c) ? `${fpOf(c)?.name} 팀장` : '', c.ceo || c.real_ceo ? `${ceoShown(c)} 대표` : ''].filter(Boolean).join(' · ');   // 호칭 「팀장」으로 통일(패널)
  const byRecent = (a: { updated_at?: string; created_at?: string }, b: { updated_at?: string; created_at?: string }) => String(b.updated_at || b.created_at || '').localeCompare(String(a.updated_at || a.created_at || ''));

  // gray = 토스식 회색 바탕 + 흰 묶음(찾기 · 한눈에 · 일정) · 그 밖 입력 화면은 흰 바탕
  const shell = (title: string, body: ReactNode, gray = false) => (
    <div className="tds-root"><div style={{ maxWidth: 640, margin: '0 auto', minHeight: '100vh', background: gray ? TOSS.bg : '#fff' }}>
      {/* 위 「‹」 = 휴대폰 뒤로 가기와 같이 한 걸음(팀장님 → 업체 → ‹ = 팀장님) */}
      <AppBar onBack={() => { if (depth.current > 0) window.history.back(); else go('home'); }} />
      {title && <Top title={title} padB={4} side={24} />}   {/* 글자선 24 = 아래 칸과 같은 선 · 한눈에 보기는 요약 줄이 제목 */}
      <div style={{ padding: gray ? '0 16px 40px' : '0 24px 40px' }}>{body}</div>
      {toastNode}
    </div></div>
  );

  if (store.loading && store.companies.length === 0) {
    return <div className="tds-root"><div style={{ padding: '120px 24px', textAlign: 'center', fontSize: 17, color: G.g600 }}>불러오는 중이에요</div></div>;
  }

  /* ── 처음 화면 — 토스 스타일(2026-10-12 밤 검토 5인 취합 · 지도사 혼자 쓰는 도구 — 설명 글 없이 빽빽하게)
   *  찾기(맨 위 · 회색 칸) · 최근 칩 → 「지금」 묶음(다음 일정 · 오늘 할 일) → 기록 · 요청 묶음 → 바로 등록 5칸 → 연락처 · PC
   *  아이콘은 연한 파랑 하나 · 빨강은 쓰지 않음 · 줄 높이 72 · 오른쪽 값은 파랑 숫자 ── */
  if (view === 'home') {
    const t0 = kstToday();
    const items = schedItems(store.consultations, lectures || [], t0, addDays(t0, 13));
    const nx = nextUp(items);
    const picks = quickPicks(store.companies, store.fps, store.consultations, 6, false);   // 최근 본 것만(오늘 상대는 아래 다음 일정과 겹침 · 검토)
    const bd = boardRows(store.tasks, store.companies);
    const left = todoCount ? todoCount.left : 0;
    const row = (k: string, icon: string, title: ReactNode, sub: ReactNode, right: ReactNode, on: () => void, aria: string) => (
      <button key={k} type="button" className="tds-row" onClick={on} aria-label={aria}
        style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', minHeight: 72, padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
        <Tint icon={icon} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: TOSS.text, ...clamp(1) }}>{title}</span>
          {sub && <span style={{ display: 'block', fontSize: 15, color: TOSS.sub, marginTop: 2, fontVariantNumeric: 'tabular-nums', ...clamp(1) }}>{sub}</span>}
        </span>
        {right}
        <Icon name="r" size={18} color={G.g400} sw={2} />
      </button>
    );
    const val = (t: string) => <span style={{ flexShrink: 0, fontSize: 17, fontWeight: 700, color: TOSS.blue, fontVariantNumeric: 'tabular-nums' }}>{t}</span>;
    const quick: { v: View; icon: string; t: string }[] = [
      { v: 'consultNew', icon: 'brief', t: '상담' }, { v: 'lectureNew', icon: 'mic', t: '강의' },
      { v: 'company', icon: 'bld', t: '업체' }, { v: 'fp', icon: 'user', t: '팀장님' },
    ];
    return (
      <div className="tds-root"><div style={{ maxWidth: 640, margin: '0 auto', padding: '0 16px 24px', minHeight: '100vh', background: TOSS.bg }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, padding: '16px 4px 12px' }}>
          <span style={{ fontSize: 26, fontWeight: 800, color: TOSS.text }}>업무 처리</span>
          <span style={{ fontSize: 15, color: TOSS.sub, ...clamp(1) }}>{md(t0)}</span>
        </div>
        <FindBar onOpen={() => go('find')} />
        {picks.length > 0 && <div style={{ margin: '10px 0 0' }}><PickChips picks={picks} onOpen={open} /></div>}
        <div style={{ height: 12 }} />
        {/* ① 고객 업무(정식 · 돈 받는 일) — 기한이 나에게 다가온 것 · 숫자 4칸(누르면 그 목록) */}
        <Card pad="4px 0 14px">
          <CardHead title="고객 업무" right={<button type="button" className="tds-btn" onClick={() => go('task')} style={{ minHeight: 44, padding: '0 4px', border: 'none', background: 'none', color: TOSS.blue, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>＋ 업무 추가</button>} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', padding: '4px 8px 0' }}>
            {BOARD_TABS.map(x => {
              const n = bd[x.k].length;
              const color = !n ? TOSS.faint : x.k === 'over' ? TOSS.red : x.k === 'today' ? TOSS.blue : TOSS.text;
              return (
                <button key={x.k} type="button" className="tds-btn" onClick={() => go('board', { tab: x.k })} aria-label={`${x.t} ${n}건`}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minHeight: 64, padding: '6px 0', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                  <span style={{ fontSize: 24, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
                  <span style={{ fontSize: 15, color: TOSS.sub, ...clamp(1) }}>{x.t}</span>
                </button>
              );
            })}
          </div>
        </Card>
        {/* 대출 링크 보내기 — 고객이 「대출 알아봐 주세요」 하면 바로(2026-10-14 지도사) */}
        <Card>
          {row('loan', 'won', '대출 링크 보내기', '팀장님 · 대표님께 대출 접수 링크', null, () => go('loan'), '대출 링크 보내기')}
        </Card>
        {/* ② 일정 */}
        <Card>
          {row('sched', 'cal', nx.item ? whenText(nx.item) : lectures === null ? '일정 불러오는 중' : '2주 안 일정 없음',
            nx.item ? `${nx.item.main}${nx.item.tail}` : '상담 · 강의 일정', nx.today > 1 ? val(`오늘 ${nx.today}`) : null, () => go('sched'), '상담 · 강의 일정')}
          {row('memo', 'memo', '상담 · 통화 메모', '업체에 남기기', null, () => go('memo'), '상담 · 통화 메모')}
          {row('call', 'mic', '통화 녹음 올리기', '대화 글로 정리 · 팀장님께 문자', null, () => go('call'), '통화 녹음 올리기')}
        </Card>
        {/* ③ 일일 업무 — 잔 업무를 수시로 적고 지우는 곳(고객 업무와 따로) */}
        <Card>
          <CardHead title="일일 업무" />
          {row('todo', 'check', '내 할 일', todoCount && todoCount.old ? `어제 넘어온 것 ${todoCount.old}` : '글 · 캡처로 적고 체크', left ? val(`${left}`) : null, () => go('todo'), left ? `내 할 일 ${left}개` : '내 할 일')}
          {row('request', 'send', '담당자에게 요청', '직원 일일 업무로 보내기', null, () => go('request'), '담당자에게 요청')}
          {row('note', 'memo', '현장 노트 적기', '배운 점 · 서비스 아이디어', null, () => go('note'), '현장 노트 적기')}
        </Card>
        <Card pad="14px 8px 12px">
          <div style={{ fontSize: 15, fontWeight: 700, color: TOSS.sub, padding: '0 12px 10px' }}>바로 등록</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 4 }}>
            {quick.map(q => (
              <button key={q.v} type="button" className="tds-btn" onClick={() => go(q.v)} aria-label={`${q.t} 등록`}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minHeight: 76, padding: '4px 0', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                <Tint icon={q.icon} size={44} />
                <span style={{ fontSize: 15, fontWeight: 600, color: TOSS.text, maxWidth: '100%', ...clamp(1) }}>{q.t}</span>
              </button>
            ))}
          </div>
        </Card>
        <Card>
          {row('contacts', 'user', '연락처 내려받기', '전화 올 때 누구인지 뜨게', null, () => go('contacts'), '연락처 내려받기')}
          <a href="/" style={{ display: 'flex', alignItems: 'center', minHeight: 56, padding: '0 20px', fontSize: 16, color: TOSS.sub, textDecoration: 'none' }}>PC 화면으로</a>
        </Card>
        {toastNode}
      </div></div>
    );
  }

  const coSel = store.companies.find(c => c.id === pre.companyId);
  const fpSel = store.fps.find(f => f.id === pre.fpId);
  const gone = (what: string) => <div style={{ padding: '24px 0', fontSize: 16, color: G.g700 }}>{what}을 찾을 수 없어요 · 지워졌을 수 있어요</div>;
  const reloadCons = async () => { await store.refreshConsultations(); };
  const reloadLec = async () => { await loadLectures(); };
  if (view === 'find') return shell('', <FindView companies={store.companies} fps={store.fps} consultations={store.consultations} compSub={compSub} onOpen={open} goNewCompany={() => go('company')} goNewFp={() => go('fp')} />, true);
  if (view === 'co') return shell('', coSel ? <CompanyView key={coSel.id} c={coSel} fps={store.fps} wms={store.wms} tasks={store.tasks} consultations={store.consultations} onFp={openFp}
    goMemo={() => go('memo', { companyId: coSel.id })} goTask={() => go('task', { companyId: coSel.id })} goConsult={() => go('consultNew', { companyId: coSel.id })}
    goCall={() => go('call', { companyId: coSel.id })} refreshTasks={store.refreshTasks} toast={toast} /> : gone('업체'), true);
  if (view === 'fpv') return shell('', fpSel ? <FpView key={fpSel.id} f={fpSel} companies={store.companies} tasks={store.tasks} consultations={store.consultations} onCompany={openCo}
    saveMemo={text => saveFpMemo(fpSel, text)} goCompany={() => go('company', { fpId: fpSel.id })} goConsult={() => go('consultNew', { fpId: fpSel.id })} /> : gone('팀장님'), true);
  if (view === 'sched') return shell('상담 · 강의 일정', <SchedView consultations={store.consultations} lectures={lectures} onCompany={openCo} goConsult={() => go('consultNew')} goLecture={() => go('lectureNew')} />, true);
  if (view === 'consultNew') return shell('상담 등록', <ConsultNewForm preCompany={pre.companyId} preFp={pre.fpId} companies={store.companies} fps={store.fps} consultations={store.consultations} lectures={lectures || []}
    compSub={compSub} byRecent={byRecent} toast={toast} onSaved={reloadCons} onCompany={openCo} goSched={() => go('sched')} />);
  if (view === 'lectureNew') return shell('강의 등록', <LectureNewForm fps={store.fps} wms={store.wms} consultations={store.consultations} lectures={lectures || []} byRecent={byRecent} toast={toast} onSaved={reloadLec} goSched={() => go('sched')} />);
  if (view === 'board') return shell('고객 업무', <BoardView key={pre.tab || 'over'} tasks={store.tasks} companies={store.companies} tab0={pre.tab || 'over'} onCompany={openCo} />, true);
  if (view === 'contacts') return shell('연락처 내려받기', <ContactsView fps={store.fps} companies={store.companies} />);
  if (view === 'call') return shell('통화 녹음 올리기', <MobileCall key={pre.companyId || 'all'} preCompany={pre.companyId} shared={pre.shared} companies={store.companies} fps={store.fps} compSub={compSub} byRecent={byRecent}
    toast={toast} onCompany={openCo} />);
  if (view === 'loan') return shell('대출 링크 보내기', <MobileLoan fps={store.fps} toast={toast} refresh={store.refresh} />, true);
  if (view === 'todo') return shell('일일 업무 · 내 할 일', <MobileTodo me={me} staff={staff} />);
  if (view === 'note') return shell('현장 노트 적기', <MobileNote toast={toast} onSaved={() => go('home')} />);
  if (view === 'memo') return shell('상담 · 통화 메모', <MemoForm pre={pre} companies={store.companies} tasks={store.tasks} compSub={compSub} byRecent={byRecent} toast={toast} go={go} refreshTasks={store.refreshTasks} />);
  if (view === 'company') return shell('소개받은 업체 등록', <CompanyForm pre={pre} fps={store.fps} wms={store.wms} byRecent={byRecent} toast={toast} go={go} refresh={store.refresh} />);
  if (view === 'fp') return shell('팀장님 등록', <FpForm pre={pre} fps={store.fps} wms={store.wms} toast={toast} go={go} refresh={store.refresh} />);
  if (view === 'task') return shell('고객 업무 추가', <TaskForm pre={pre} companies={store.companies} tasks={store.tasks} templates={store.taskTemplates} compSub={compSub} byRecent={byRecent} toast={toast} go={go} refresh={store.refreshTasks} />);
  return shell('일일 업무 · 담당자에게 요청', <RequestForm pre={pre} me={me} staff={staff} reloadStaff={loadStaff} companies={store.companies} compSub={compSub} byRecent={byRecent} toast={toast} go={go} />);
}

type Go = (v: View, p?: Pre) => void;

/* 1. 상담 · 통화 메모 */
function MemoForm({ pre, companies, tasks, compSub, byRecent, toast, go, refreshTasks }: { pre: Pre; companies: Company[]; tasks: Task[]; compSub: (c: Company) => string; byRecent: ByRecent; toast: (m: string) => void; go: Go; refreshTasks: () => Promise<void> }) {
  const [cid, setCid] = useState<string | null>(pre.companyId || null);
  const [kind, setKind] = useState('통화');
  const [text, setText] = useState(pre.back === 'memo' ? pre.text || '' : '');   // 새 업체 등록하고 돌아오면 적던 글 그대로
  const [show, setShow] = useState<string[]>([]);   // 'fp' · 'cust' — 비우면 나만(저장 뒤 · 업체 바꾸면 다시 나만)
  const [by, setBy] = useState<Record<string, string>>({});   // 답한 사람(업체 · FP) — 둘 다 기다릴 때만 묻는다
  const [lastPub, setLastPub] = useState<string | null>(null);   // 방금 대표님 화면에 올린 메모 id
  const [replied, setReplied] = useState<string[]>([]);   // 이 통화로 답을 받은 업무
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<{ cid: string; text: string } | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const today = kstToday();
  // 고른 업체의 회신 대기 업무 — 통화로 답을 받았으면 여기서 바로 「회신 받음」(대시보드까지 안 가도 되게)
  const waiting = cid ? tasks.filter(t => t.company_id === cid && t.status === '진행').map(t => ({ t, s: curStep(t) })).filter(x => x.s && x.s.status === '회신대기') : [];
  const pick = (id: string | null) => { setCid(id); setReplied([]); setBy({}); setShow([]); if (id) window.setTimeout(() => { if (taRef.current) taRef.current.focus(); }, 50); };
  const whoOf = (x: { s: TaskStep | null }): string[] => { const w = stepEx(x.s).wait_for; return w && w.length ? w : ['업체', 'FP']; };
  const needBy = waiting.filter(x => replied.includes(x.t.id) && whoOf(x).length > 1 && !by[x.t.id]);
  // 회신 받음만 바꿀 때는 내용이 없어도 된다
  const missing = !cid ? '업체를 고르세요' : needBy.length ? '누가 답했는지 고르세요' : !text.trim() && !replied.length ? '내용을 적어 주세요' : '';
  const save = async () => {
    if (!guard(missing, busy, toast) || !cid) return;
    setBusy(true);
    try {
      let pubId: string | null = null;
      if (text.trim()) {
        const r = await api.createComment({ company_id: cid, content: `[${kind}] ${text.trim()}`, fp_visible: show.includes('fp'), customer_visible: show.includes('cust') });
        if (!r.success) throw new Error(r.error || '저장하지 못했어요');
        if (show.includes('cust') && r.data) pubId = r.data.id;
      }
      let fail = 0;
      for (const x of waiting.filter(w => replied.includes(w.t.id))) {
        const who = whoOf(x).length > 1 ? by[x.t.id] : whoOf(x)[0];
        try { await api.taskFlow(x.t.id, { action: 'reply', stepId: (x.s as TaskStep).id, by: who }); } catch { fail += 1; }
      }
      if (replied.length) await refreshTasks().catch(() => undefined);
      const memoMsg = !text.trim() ? '' : pubId ? '메모를 남기고 대표님 화면에 올렸어요' : '메모를 남겼어요';
      toast(fail ? '회신 받음 표시는 PC 에서 다시 해 주세요' : [memoMsg, replied.length ? '회신 받음으로 바꿨어요' : ''].filter(Boolean).join(' · '));
      setSaved({ cid, text: text.trim() }); setText(''); setReplied([]); setBy({}); setShow([]); setLastPub(pubId);
    } catch (e: unknown) { toast(e instanceof Error ? e.message : '저장하지 못했어요'); }
    finally { setBusy(false); }
  };
  const preview = text.trim();
  return (
    <>
      <Field title="업체" need><Picker items={companies} value={cid} onChange={pick} sub={compSub} placeholder="업체 이름으로 찾기" recent={byRecent}
        extra={(q: string) => <ListRow title={<span style={{ color: NAVY }}>＋ {q ? `「${short(q, 12)}」 새 업체로 등록` : '새 업체 등록'}</span>} onClick={() => go('company', { text, back: 'memo' })} pad="10px 14px" />} /></Field>
      <Field title="종류"><Chips cols={4} items={[{ k: '상담', t: '상담' }, { k: '통화', t: '통화' }, { k: 'FP 요청', t: 'FP 요청' }, { k: '메모', t: '메모' }]} value={[kind]} onChange={v => setKind(v[0])} /></Field>
      {waiting.length > 0 && (
        <Field title="회신 대기 중인 업무">
          {waiting.map(x => {
            const d = Math.max(0, diffDays(String(stepEx(x.s).wait_since || today).slice(0, 10), today));
            const on = replied.includes(x.t.id);
            return (
              <div key={x.t.id}>
                <Check on={on} onChange={v => setReplied(c => (v ? [...c, x.t.id] : c.filter(id => id !== x.t.id)))}>답을 받았어요 · {short(x.t.type, 12)} 「{short((x.s as TaskStep).name, 10)}」 <span style={{ whiteSpace: 'nowrap' }}>답 기다린 지 {d}일</span></Check>
                {on && whoOf(x).length > 1 && (
                  <div style={{ margin: '0 0 8px 30px' }}>
                    <div style={{ fontSize: 15, color: G.g700, marginBottom: 6 }}>누가 답했나요 <span style={{ color: '#D22030' }}>*</span></div>
                    <Chips cols={2} items={[{ k: '업체', t: '대표님' }, { k: 'FP', t: '팀장님' }]} value={by[x.t.id] ? [by[x.t.id]] : []} onChange={v => setBy(b => ({ ...b, [x.t.id]: v[0] }))} />
                  </div>
                )}
              </div>
            );
          })}
        </Field>
      )}
      <Field title="내용" need={!replied.length} note={replied.length ? '회신 받음만 바꿀 때는 비워도 돼요' : undefined}>
        <textarea ref={taRef} value={text} onChange={e => setText(e.target.value)} rows={5} placeholder="통화 내용 · 요청받은 것 · 다음에 할 것"
          style={{ ...input, height: 'auto', minHeight: 120, padding: 14, lineHeight: 1.6, resize: 'vertical' }} />
      </Field>
      <Field title="보이는 곳" note={show.length ? undefined : '아무것도 안 고르면 나만 봐요'}>
        <Chips multi cols={2} items={[{ k: 'fp', t: '팀장님 화면에도' }, { k: 'cust', t: '대표님 화면에도' }]} value={show} onChange={setShow} />
      </Field>
      {show.includes('cust') && (
        <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 12, background: G.g100 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: G.g700, marginBottom: 4 }}>대표님 화면에 이렇게 보여요</div>
          <div style={{ fontSize: 16, color: G.g900, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>{preview || '(내용을 적으면 여기에 보여요)'}</div>
        </div>
      )}
      <SaveBar><Button h={56} muted={!!missing && !busy} onClick={() => void save()}>{busy ? '저장 중' : missing || (!text.trim() ? '회신 받음으로 바꾸기' : show.includes('cust') ? '메모 저장 · 대표님 화면에 올림' : '메모 저장')}</Button></SaveBar>
      {saved && <Next items={[
        ...(lastPub ? [{ t: '방금 메모 대표님 화면에서 내리기', on: () => { const id = lastPub; setLastPub(null); void api.updateComment(id, { customer_visible: false }).then(() => toast('대표님 화면에서 내렸어요')).catch(() => toast('내리지 못했어요 · PC 에서 다시 해 주세요')); } }] : []),
        { t: '이 메모로 업무 등록', on: () => go('task', { companyId: saved.cid, text: saved.text }) },
        { t: '담당자에게 요청', on: () => go('request', { companyId: saved.cid, text: saved.text }) },
        { t: '처음으로', on: () => go('home') },
      ]} />}
    </>
  );
}

/* 2. FP 밑에 업체 등록(간이) */
function CompanyForm({ pre, fps, wms, byRecent, toast, go, refresh }: { pre: Pre; fps: FP[]; wms: { id: string; name: string; company: string }[]; byRecent: ByRecent; toast: (m: string) => void; go: Go; refresh: () => Promise<void> }) {
  const [fpId, setFpIdRaw] = useState<string | null>(pre.fpId || null);
  const [wmPick, setWmPick] = useState('');   // FP 에 WM 이 없을 때: WM id 또는 'none' (PC 업체 등록과 같은 규칙)
  const setFpId = (id: string | null) => { setFpIdRaw(id); setWmPick(''); };
  // 적던 칸은 「새 FP 등록」에 다녀와도 그대로(이 탭에만 잠깐 보관 · 등록하면 지움 · 2026-10-12)
  const draft0 = ((): { name?: string; ceo?: string; phone?: string; bizNo?: string } => { try { return JSON.parse(sessionStorage.getItem('m-company-draft') || '{}'); } catch { return {}; } })();
  const [name, setName] = useState(draft0.name || '');
  const [ceo, setCeo] = useState(draft0.ceo || '');
  const [phone, setPhone] = useState(draft0.phone || '');
  const [bizNo, setBizNo] = useState(draft0.bizNo || '');
  useEffect(() => {
    try { if (name || ceo || phone || bizNo) sessionStorage.setItem('m-company-draft', JSON.stringify({ name, ceo, phone, bizNo })); else sessionStorage.removeItem('m-company-draft'); } catch { /* 보관 못 해도 입력은 그대로 */ }
  }, [name, ceo, phone, bizNo]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ id: string; name: string } | null>(null);
  const fp = fps.find(f => f.id === fpId);
  const wm = fp && fp.wm_id ? wms.find(w => w.id === fp.wm_id) : undefined;
  const needWm = !!fp && !fp.wm_id;
  const missing = !fpId ? 'FP 를 고르세요' : needWm && !wmPick ? 'WM 을 고르거나 「WM 없음」을 고르세요' : !name.trim() ? '업체명을 적어 주세요' : '';
  const save = async () => {
    if (!guard(missing, busy, toast) || !fpId) return;
    setBusy(true);
    try {
      const wmId = fp && fp.wm_id ? fp.wm_id : wmPick && wmPick !== 'none' ? wmPick : null;
      const r = await api.createCompanyOrLink({
        name: name.trim(), ceo: ceo.trim(), ceo_phone: phone.trim(), biz_no: bizNo.replace(/[^0-9]/g, ''),
        fp_ids: [fpId], wm_id: wmId, tm_status: '대기', source: '모바일 간이 등록', referral_type: 'FP',
      } as Parameters<typeof api.createCompanyOrLink>[0]);
      // 고른 WM 은 FP 에도 — 다음부터 자동 (업체 등록이 된 뒤에)
      let wmFail = false;
      if (needWm && fp && wmId) { try { await api.updateFp(fp.id, { wm_id: wmId }); } catch { wmFail = true; } }
      if (r.existed) { toast(`이미 있는 업체예요 · ${r.existingName || name}`); setDone({ id: r.id, name: r.existingName || name }); setName(''); setCeo(''); setPhone(''); setBizNo(''); await refresh(); return; }
      await refresh();
      toast(wmFail ? '업체는 등록했어요 · FP 에 WM 저장은 못 했어요' : '업체를 등록했어요'); setDone({ id: r.id, name: name.trim() });
      setName(''); setCeo(''); setPhone(''); setBizNo('');
    } catch (e: unknown) { toast(e instanceof Error ? e.message : '등록하지 못했어요'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Field title="소개한 FP" need>
        <Picker items={fps.filter(f => f.is_active !== false)} value={fpId} onChange={setFpId} sub={f => f.company || ''} placeholder="FP 이름 · 소속으로 찾기" recent={byRecent}
          extra={(q: string) => <ListRow title={<span style={{ color: NAVY }}>＋ {q ? `「${short(q, 10)}」 새 FP 로 등록` : '새 FP 등록'}</span>} onClick={() => go('fp', { text: q, back: 'company' })} pad="10px 14px" />} />
      </Field>
      {fp && !needWm && <div style={{ fontSize: 15, color: G.g700, marginTop: 8 }}>담당 WM · <b style={{ color: G.g900 }}>{wm ? `${wm.name}${wm.company ? ' ' + wm.company : ''}` : ''}</b> <span style={{ color: G.g600 }}>(FP 에서 자동)</span></div>}
      {needWm && fp && (
        <Field title={`${fp.name} FP 의 WM`} need note="고르면 FP 에도 저장돼 다음부터 자동이에요">
          <select value={wmPick} onChange={e => setWmPick(e.target.value)} style={{ ...input, color: wmPick ? G.g900 : G.g600 }}>
            <option value="">고르세요</option>
            <option value="none">WM 없음</option>
            {wms.map(w => <option key={w.id} value={w.id}>{w.name}{w.company ? ` · ${w.company}` : ''}</option>)}
          </select>
        </Field>
      )}
      <Field title="업체명" need><input value={name} onChange={e => setName(e.target.value)} placeholder="(주)○○" style={input} /></Field>
      <Field title="대표자"><input value={ceo} onChange={e => setCeo(e.target.value)} placeholder="이름" style={input} /></Field>
      <Field title="대표 연락처"><input value={phone} onChange={e => setPhone(e.target.value)} placeholder="010-0000-0000" inputMode="tel" style={input} /></Field>
      <Field title="사업자번호" note="몰라도 돼요 · 나중에 PC 에서 조회로 채워요"><input value={bizNo} onChange={e => setBizNo(e.target.value)} placeholder="000-00-00000" inputMode="numeric" style={input} /></Field>
      <SaveBar><Button h={56} muted={!!missing && !busy} onClick={() => void save()}>{busy ? '등록 중' : missing || '업체 등록'}</Button></SaveBar>
      {done && <Next items={[
        ...(pre.back === 'memo' ? [{ t: `「${short(done.name, 14)}」 메모로 돌아가기`, on: () => go('memo', { companyId: done.id, text: pre.text, back: 'memo' as const }) }] : []),
        { t: `「${short(done.name, 14)}」에 업무 등록`, on: () => go('task', { companyId: done.id }) },
        { t: `「${short(done.name, 14)}」 메모 남기기`, on: () => go('memo', { companyId: done.id }) },
        { t: '같은 FP 밑에 업체 하나 더', on: () => setDone(null) },
      ]} />}
    </>
  );
}

/* 3. FP 등록(간이) */
function FpForm({ pre, fps, wms, toast, go, refresh }: { pre: Pre; fps: FP[]; wms: { id: string; name: string; company: string }[]; toast: (m: string) => void; go: Go; refresh: () => Promise<void> }) {
  const [name, setName] = useState(pre.back === 'company' ? pre.text || '' : '');   // 업체 등록에서 찾던 이름 그대로
  const [phone, setPhone] = useState('');
  const [org, setOrg] = useState('');
  const [wmId, setWmId] = useState<string>('');   // '' = 아직 안 고름 · 'none' = WM 없음
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<FP | null>(null);
  const orgs = useMemo(() => Array.from(new Set(fps.map(f => f.company).filter(Boolean))).sort(), [fps]);
  const dup = phone.replace(/[^0-9]/g, '').length >= 10 ? fps.find(f => (f.phone || '').replace(/[^0-9]/g, '') === phone.replace(/[^0-9]/g, '')) : undefined;
  const missing = !name.trim() ? '이름을 적어 주세요' : !phone.trim() ? '휴대폰을 적어 주세요' : dup ? '이미 등록된 번호예요' : !wmId ? 'WM 을 고르거나 「WM 없음」을 고르세요' : '';
  const save = async () => {
    if (!guard(missing, busy, toast)) return;
    setBusy(true);
    try {
      const r = await api.createFp({ name: name.trim(), phone: phone.trim(), company: org.trim(), wm_id: wmId && wmId !== 'none' ? wmId : null });
      if (!r.success || !r.data) throw new Error(r.error || '등록하지 못했어요');
      await refresh();
      // 업체 등록 중에 왔으면 새 FP 가 골라진 채로 바로 돌아간다
      if (pre.back === 'company') { toast(`「${r.data.name}」 FP 를 등록했어요 · 업체 등록으로 돌아왔어요`); go('company', { fpId: r.data.id }); return; }
      toast('FP 를 등록했어요'); setDone(r.data); setName(''); setPhone(''); setWmId('');
    } catch (e: unknown) { toast(e instanceof Error ? e.message : '등록하지 못했어요'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Field title="이름" need><input value={name} onChange={e => setName(e.target.value)} placeholder="홍길동" style={input} /></Field>
      <Field title="휴대폰" need><input value={phone} onChange={e => setPhone(e.target.value)} placeholder="010-0000-0000" inputMode="tel" style={input} /></Field>
      {dup && <div style={{ fontSize: 15, color: '#D22030', marginTop: 6 }}>이미 등록된 번호예요 · {dup.name}{dup.company ? ` · ${dup.company}` : ''} <button type="button" className="tds-btn" onClick={() => go('company', { fpId: dup.id })} style={{ minHeight: 44, border: 'none', background: 'none', color: NAVY, fontSize: 15, fontWeight: 700, cursor: 'pointer', padding: '0 4px' }}>이 FP 밑에 업체 등록 ›</button></div>}
      <Field title="소속" note="보험사 · 지점 · 전에 쓴 소속이 아래에 나와요">
        <input value={org} onChange={e => setOrg(e.target.value)} list="m-fp-orgs" placeholder="한화생명금융서비스 ○○지점" style={input} />
        <datalist id="m-fp-orgs">{orgs.map(o => <option key={o} value={o} />)}</datalist>
      </Field>
      <Field title="담당 WM" need>
        <select value={wmId} onChange={e => setWmId(e.target.value)} style={{ ...input, color: wmId ? G.g900 : G.g600 }}>
          <option value="">고르세요</option>
          <option value="none">WM 없음</option>
          {wms.map(w => <option key={w.id} value={w.id}>{w.name}{w.company ? ` · ${w.company}` : ''}</option>)}
        </select>
      </Field>
      <SaveBar><Button h={56} muted={!!missing && !busy} onClick={() => void save()}>{busy ? '등록 중' : missing || '팀장님 등록'}</Button></SaveBar>
      {done && <Next items={[
        { t: `「${short(done.name, 14)}」 FP 밑에 업체 등록`, on: () => go('company', { fpId: done.id }) },
        { t: '처음으로', on: () => go('home') },
      ]} />}
    </>
  );
}

/* 4. 업체 밑에 업무 등록 */
/** 자료 요청 — 이 업체에 업무에 안 이어진 자료수집이 있을 때만(없으면 칸도 없음 · 2026-10-28) */
function CollectLinkPickerBox({ companyId, value, onChange }: { companyId: string; value: string; onChange: (id: string) => void }) {
  return (
    <div style={{ marginTop: 4 }}>
      <CollectLinkPicker look="mobile" companyId={companyId} value={value} onChange={id => onChange(id)} />
    </div>
  );
}

function TaskForm({ pre, companies, tasks, templates, compSub, byRecent, toast, go, refresh }: {
  pre: Pre; companies: Company[]; tasks: { company_id: string; status: string; type?: string }[]; templates: TaskTemplate[]; compSub: (c: Company) => string; byRecent: ByRecent;
  toast: (m: string) => void; go: Go; refresh: () => Promise<void>;
}) {
  const [cid, setCid] = useState<string | null>(pre.companyId || null);
  const [tplId, setTplId] = useState<string | null>(null);
  const [custom, setCustom] = useState('');
  const [q, setQ] = useState('');
  const [first, setFirst] = useState(addDays(kstToday(), 3));
  const [turn, setTurn] = useState<'' | 'mine' | 'wait'>('');   // 기본값 없음 — 꼭 고르게(PC 상담 결과와 같은 규칙)
  const [who, setWho] = useState<string[]>(['업체', 'FP']);
  const [ask, setAsk] = useState('');   // 회신 대기 — 대표님 · 팀장님께 물을 말(메모 글이 그대로 질문으로 나가던 것 · 2차 검수)
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [force, setForce] = useState(false);
  const [linkId, setLinkId] = useState('');   // 자료수집 단계 — 기존 자료 요청 잇기(2026-10-28)
  const tpl = templates.find(t => t.id === tplId);
  /* 세부 단계 고치기 (2026-10-12 지도사 「템플릿 등록도 하지만 세부단계 수정도 가능해야지」) — null = 템플릿 그대로 · 고치면 이 업무에만(템플릿은 그대로)
     days = 첫 단계로부터 며칠 뒤(템플릿 간격) */
  type EStep = { k: string; name: string; days: number; type: string; note: string };
  const [edited, setEdited] = useState<EStep[] | null>(null);
  const [stepsOpen, setStepsOpen] = useState(false);
  useEffect(() => { setEdited(null); setStepsOpen(false); }, [tplId]);
  const baseSteps = (): EStep[] => {
    const raw = tpl && tpl.steps && tpl.steps.length ? tpl.steps : [{ name: custom.trim(), type: 'work', due_days: 0, note: '' }];
    const off0 = Number(raw[0].due_days) || 0;
    return raw.map((st, i) => ({ k: 's' + i, name: st.name, days: Math.max(0, (Number(st.due_days) || 0) - off0), type: st.type || 'work', note: st.note || '' }));
  };
  const steps0 = edited || baseSteps();
  const editSteps = (fn: (a: EStep[]) => EStep[]) => setEdited(fn((edited || baseSteps()).map(x => ({ ...x }))));
  const lastDays = steps0.reduce((m, x) => Math.max(m, x.days), 0);
  const comp = companies.find(c => c.id === cid);
  const active = templates.filter(t => t.is_active !== false);
  const shown = (q.trim() ? active.filter(t => t.name.includes(q.trim()) || (t.cat_path || []).join(' ').includes(q.trim())) : active).slice(0, 12);
  const name = tpl ? tpl.name : custom.trim();
  const dup = cid && name ? (tasks as { company_id: string; status: string; type?: string }[]).find(t => t.company_id === cid && t.type === name && (t.status === '진행' || t.status === 'FP요청대기' || t.status === '보류')) : undefined;
  const missing = !cid ? '업체를 고르세요' : !name ? '업무를 고르세요' : steps0.some(x => !x.name.trim()) ? '이름이 빈 단계가 있어요' : !first ? '첫 단계 날짜를 고르세요' : !turn ? '누구 차례인지 고르세요' : turn === 'wait' && !who.length ? '업체나 FP 를 고르세요' : turn === 'wait' && !ask.trim() ? '물을 말을 적어 주세요' : '';
  const save = async () => {
    if (!guard(missing, busy, toast) || !cid) return;
    setBusy(true);
    try {
      const src = (edited || baseSteps()).map((x, i) => ({ ...x, i })).sort((a, b) => a.days - b.days || a.i - b.i);   // 날짜 순 = 단계 순(첫 단계는 0)
      const linkAt = linkId ? src.findIndex(s => s.type === 'collect') : -1;   // 첫 자료수집 단계에 잇기
      const steps: Partial<TaskStep>[] = src.map((s, i) => ({
        name: (s.name.trim() || (tpl ? '' : name)), type: (s.type || 'work') as TaskStep['type'], status: (i === 0 ? '진행' : '대기') as TaskStep['status'],
        due: addDays(first, Math.max(0, s.days)), note: s.note || '',
        ...(i === linkAt ? { extras: { collect_ids: [], custom_docs: [], collect_sent: false, collect_project_id: null, link_project_id: linkId } } : {}),
      }));
      const r = await api.createTask({
        // template_id — 서버가 템플릿 단계 설정(담당 · 안내 글 · 받을 서류)을 옮겨 적는다(휴대폰에서 빠지던 것 · 2026-10-28 검토)
        company_id: cid, template_id: tpl ? tpl.id : null, type: name, icon: (tpl && tpl.icon) || '📋', category_path: tpl ? tpl.cat_path || [] : [],
        status: '진행', deadline: steps[steps.length - 1].due || null, steps,
      });
      if (!r.success || !r.data) throw new Error(r.error || '등록하지 못했어요');
      const t = r.data;
      // 업무는 이미 만들어졌다 — 회신 대기 표시만 실패하면 「등록 못함」이라 하지 않는다(다시 누르면 중복)
      let waitFail = false;
      if (turn === 'wait' && t.steps && t.steps[0]) {
        try { await api.taskFlow(t.id, { action: 'wait', stepId: t.steps[0].id, wait_for: who, question: ask.trim().slice(0, 200) }); } catch { waitFail = true; }
      }
      // 메모는 메모 화면에서 이미 저장됨 — 여기서 다시 저장하지 않는다(같은 글이 두 번 남던 것)
      await refresh();
      toast(waitFail ? '업무는 등록했어요 · 회신 대기 표시는 PC 에서 다시 해 주세요' : `${comp ? short(comp.name, 12) : ''}에 「${short(name, 14)}」을 등록했어요`); setDone(cid);
      setTplId(null); setCustom(''); setQ(''); setTurn(''); setAsk(''); setForce(false); setEdited(null); setStepsOpen(false); setLinkId('');   // 다시 눌러 같은 업무가 또 생기지 않게
    } catch (e: unknown) { toast(e instanceof Error ? e.message : '등록하지 못했어요'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Field title="업체" need><Picker items={companies} value={cid} onChange={setCid} sub={compSub} placeholder="업체 이름으로 찾기" recent={byRecent} /></Field>
      {comp && (() => { const ln = companyLine(comp, tasks as never); const n = tasks.filter(t => t.company_id === comp.id && (t.status === '진행' || t.status === 'FP요청대기')).length; return <div style={{ fontSize: 14, color: G.g700, marginTop: 8, ...clamp(2) }}>진행 중인 업무 {n}건{ln ? ` · ${ln.text}` : ''}</div>; })()}
      {pre.text && <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: G.g100, fontSize: 15, color: G.g700, lineHeight: 1.6, ...clamp(4) }}>참고 · 방금 메모 · {pre.text}</div>}
      <Field title="업무" need>
        {tpl ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 56, padding: '6px 6px 6px 14px', borderRadius: 14, background: NAVY + '0F', border: `1px solid ${NAVY}33` }}>
            <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 17, fontWeight: 700, ...clamp(1) }}>{tpl.icon} {tpl.name}</div><div style={{ fontSize: 14, color: G.g700, ...clamp(1) }}>{tpl.steps.length}단계 · {tpl.steps.map(s => s.name).slice(0, 3).join(' → ')}{tpl.steps.length > 3 ? ' …' : ''}</div></div>
            <button type="button" className="tds-btn" onClick={() => setTplId(null)} style={{ minHeight: 44, padding: '0 14px', border: 'none', background: 'none', color: NAVY, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>바꾸기</button>
          </div>
        ) : (
          <>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="템플릿 찾기 (정책자금 · 벤처 …)" style={input} />
            <div style={{ marginTop: 6, borderRadius: 14, border: `1px solid ${G.g200}`, overflow: 'hidden' }}>
              {shown.map(t => <ListRow key={t.id} title={`${t.icon || '📋'} ${t.name}`} sub={`${t.steps.length}단계${(t.cat_path || []).length ? ' · ' + t.cat_path.join(' › ') : ''}`} arrow onClick={() => { setTplId(t.id); setCustom(''); }} clip pad="10px 14px" />)}
              {shown.length === 0 && <div style={{ padding: 14, fontSize: 15, color: G.g600 }}>맞는 템플릿이 없어요 · 아래에 직접 적어도 돼요</div>}
            </div>
            <input value={custom} onChange={e => setCustom(e.target.value)} placeholder="또는 직접 입력 (한 단계 업무)" style={{ ...input, marginTop: 10 }} />
          </>
        )}
      </Field>
      {dup && <div style={{ marginTop: 8, fontSize: 15, fontWeight: 600, color: '#9A5B00' }}>이미 진행 중인 같은 업무가 있어요 · 「{short(name, 14)}」</div>}
      <Field title="첫 단계 목표일" need note={steps0.length > 1 ? `뒤 단계는 ${edited ? '고친' : '템플릿'} 간격대로 · 마지막 ${md(addDays(first, lastDays))}` : undefined}>
        <DatePick value={first} onChange={setFirst} />
      </Field>
      {name && (
        <Field title={`단계 ${steps0.length}개`} note={edited ? '이 업무에만 바뀌어요 · 템플릿은 그대로' : undefined}>
          {!stepsOpen ? (
            <button type="button" className="tds-btn" onClick={() => setStepsOpen(true)}
              style={{ width: '100%', minHeight: 52, display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', borderRadius: 14, border: `1px solid ${G.g300}`, background: '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 16, color: G.g800, ...clamp(1) }}>{steps0.map(x => x.name || '(이름 없음)').join(' → ')}</span>
              <span style={{ flexShrink: 0, fontSize: 15, fontWeight: 700, color: NAVY }}>단계 고치기</span>
            </button>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {steps0.map((st, i) => (
                <div key={st.k} style={{ display: 'grid', gridTemplateColumns: '24px minmax(0,1fr) 92px 40px', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: G.g700, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
                  <input className="tds-in" value={st.name} maxLength={60} aria-label={`${i + 1}단계 이름`} placeholder="단계 이름"
                    onChange={e => { const v = e.target.value; editSteps(a => a.map(x => (x.k === st.k ? { ...x, name: v } : x))); }}
                    style={{ ...input, height: 48, fontSize: 16, padding: '0 10px' }} />
                  <label style={{ display: 'flex', alignItems: 'center', height: 48, border: `1px solid ${G.g500}`, borderRadius: 14, padding: '0 8px', boxSizing: 'border-box', background: i === 0 ? G.g100 : '#fff' }}>
                    <span style={{ fontSize: 14, color: G.g600, flexShrink: 0 }}>+</span>
                    <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={3} value={String(st.days)} disabled={i === 0} aria-label={`${i + 1}단계 — 첫 단계로부터 며칠 뒤`}
                      onChange={e => { const v = Math.max(0, Math.min(365, Number(e.target.value.replace(/[^0-9]/g, '')) || 0)); editSteps(a => a.map(x => (x.k === st.k ? { ...x, days: v } : x))); }}
                      style={{ width: '100%', minWidth: 0, border: 'none', outline: 'none', fontSize: 16, textAlign: 'right', background: 'transparent', fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums' }} />
                    <span style={{ fontSize: 14, color: G.g600, flexShrink: 0, marginLeft: 2 }}>일</span>
                  </label>
                  <button type="button" className="tds-btn" aria-label={`${i + 1}단계 빼기`} disabled={steps0.length <= 1}
                    onClick={() => editSteps(a => a.filter(x => x.k !== st.k).map((x, j) => (j === 0 ? { ...x, days: 0 } : x)))}
                    style={{ height: 44, border: 'none', background: 'none', color: steps0.length <= 1 ? G.g300 : G.g600, fontSize: 20, cursor: steps0.length <= 1 ? 'default' : 'pointer' }}>✕</button>
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: edited ? '1fr 1fr' : '1fr', gap: 8, marginTop: 4 }}>
                <button type="button" className="tds-btn" onClick={() => editSteps(a => [...a, { k: 'n' + Date.now(), name: '', days: (a.reduce((m, x) => Math.max(m, x.days), 0)) + 7, type: 'work', note: '' }])}
                  style={{ minHeight: 48, borderRadius: 14, border: `1px dashed ${G.g500}`, background: '#fff', color: NAVY, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>+ 단계 더하기</button>
                {edited && <button type="button" className="tds-btn" onClick={() => setEdited(null)}
                  style={{ minHeight: 48, borderRadius: 14, border: `1px solid ${G.g300}`, background: '#fff', color: G.g700, fontSize: 16, cursor: 'pointer', fontFamily: 'inherit' }}>템플릿대로 되돌리기</button>}
              </div>
              <div style={{ fontSize: 14, color: G.g600 }}>오른쪽 숫자 = 첫 단계로부터 며칠 뒤 · 단계 순서는 날짜 순으로 저장돼요</div>
            </div>
          )}
        </Field>
      )}
      {cid && steps0.some(x => x.type === 'collect') && (
        <CollectLinkPickerBox companyId={cid} value={linkId} onChange={setLinkId} />
      )}
      <Field title="첫 단계 누구 차례" need>
        <Chips cols={2} items={[{ k: 'mine', t: '내 차례' }, { k: 'wait', t: '회신 대기' }]} value={turn ? [turn] : []} onChange={v => setTurn(v[0] as 'mine' | 'wait')} />
        {turn === 'wait' && <div style={{ marginTop: 8 }}>
          <Check on={who.includes('업체')} onChange={v => setWho(c => (v ? [...c, '업체'] : c.filter(x => x !== '업체')))}>업체</Check>
          <Check on={who.includes('FP')} onChange={v => setWho(c => (v ? [...c, 'FP'] : c.filter(x => x !== 'FP')))}>FP</Check>
          <div style={{ fontSize: 15, color: TEAL }}>둘 중 누구든 먼저 답하면 끝 · 기한 경과에 안 들어가요</div>
          <div style={{ ...label, margin: '14px 0 8px' }}>대표님 · 팀장님께 물을 말 <span style={{ color: '#D22030' }}>*</span></div>
          <textarea className="tds-in" value={ask} onChange={e => setAsk(e.target.value)} rows={3} maxLength={200} placeholder="예: 감정평가 견적대로 진행할지 알려 주세요"
            style={{ ...input, height: 'auto', minHeight: 90, padding: 14, lineHeight: 1.6, resize: 'vertical' }} />
          {ask.trim() && (
            <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 12, background: G.g100 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: G.g700, marginBottom: 4 }}>대표님 화면에 이렇게 보여요</div>
              <div style={{ fontSize: 16, color: G.g900, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>{ask.trim()}</div>
            </div>
          )}
        </div>}
      </Field>
      <SaveBar><Button h={56} muted={!!missing && !busy} onClick={() => { if (dup && !force && !missing) { setForce(true); toast('이미 진행 중인 같은 업무가 있어요 · 한 번 더 누르면 등록해요'); return; } void save(); }}>{busy ? '등록 중' : missing || (dup ? (force ? '그래도 업무 등록' : '업무 등록 (같은 업무 있음)') : turn === 'wait' ? '업무 등록 · 대표님께 질문 올림' : '업무 등록')}</Button></SaveBar>
      {done && <Next items={[
        { t: '같은 업체에 업무 하나 더', on: () => setDone(null) },
        { t: '담당자에게 요청', on: () => go('request', { companyId: done, text: name }) },
        { t: '처음으로', on: () => go('home') },
      ]} />}
    </>
  );
}

/* 5. 담당자에게 업무 요청 — 일일 업무(담당자 지정) */
function RequestForm({ pre, me, staff, reloadStaff, companies, compSub, byRecent, toast, go }: { pre: Pre; me: Me | null; staff: Staff[] | null; reloadStaff: () => void; companies: Company[]; compSub: (c: Company) => string; byRecent: ByRecent; toast: (m: string) => void; go: Go }) {
  const [to, setTo] = useState<string[]>([]);
  const [cid, setCid] = useState<string | null>(pre.companyId || null);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState(pre.text || '');
  const [due, setDue] = useState(addDays(kstToday(), 1));   // 기본 = 내일(오늘이면 다음 날 바로 「지남」)
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  // 첨부(2026-10-13 지도사 「파일도 첨부 — PDF 든 JPG 든」) — 사진은 줄여서 · 한 파일 50MB · 10개까지
  const [files, setFiles] = useState<File[]>([]);
  const [prog, setProg] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const addFiles = (list: File[]) => {
    const big = list.filter(f => f.size > 50 * 1024 * 1024);
    if (big.length) toast(`50MB 넘는 파일은 못 올려요 · ${short(big[0].name, 14)}`);
    setFiles(fs => [...fs, ...list.filter(f => f.size <= 50 * 1024 * 1024)].slice(0, 10));
  };
  const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`);
  const comp = companies.find(c => c.id === cid);
  // 「나」를 맨 앞 칩으로(체크박스 따로 X)
  const people = (staff || []).slice().sort((a, b) => (me && a.id === me.id ? -1 : me && b.id === me.id ? 1 : 0)).map(s => ({ k: s.id, t: me && s.id === me.id ? '나' : s.name }));
  const missing = !to.length ? '담당자를 고르세요' : !title.trim() ? '할 일을 적어 주세요' : '';
  // 버튼 · 알림 = 누구에게 갔는지 그대로
  const nameOf = (id: string) => (me && id === me.id ? '나' : ((staff || []).find(x => x.id === id) || { name: '' }).name);
  const toLabel = to.length === 1 && me && to[0] === me.id ? '내 일일 업무에 넣었어요' : to.length === 1 ? `${nameOf(to[0])}에게 요청했어요` : `${nameOf(to.filter(x => !me || x !== me.id)[0] || to[0])} 외 ${to.length - 1}명에게 요청했어요`;
  const toBtn = to.length === 1 && me && to[0] === me.id ? '내 일일 업무에 넣기' : to.length === 1 ? `${nameOf(to[0])}에게 요청` : `${nameOf(to.filter(x => !me || x !== me.id)[0] || to[0])} 외 ${to.length - 1}명에게 요청`;
  const save = async () => {
    if (!guard(missing, busy, toast)) return;
    setBusy(true);
    try {
      const r = await todosApi.createTodo({ title: `${comp ? `[${comp.name}] ` : ''}${title.trim()}`, description: desc.trim() || undefined, assignees: to, due_date: due || null });
      if (!r.success || !r.data) throw new Error(r.error || '보내지 못했어요');
      let fail = 0;
      for (let i = 0; i < files.length; i += 1) {
        setProg(`${i + 1}/${files.length}`);
        try { const u = await todosApi.uploadAttachment(r.data.id, await shrink(files[i])); if (!u.success) fail += 1; } catch { fail += 1; }
      }
      toast(`${toLabel}${files.length ? ` · 파일 ${files.length - fail}개` : ''}${fail ? ` · ${fail}개 못 올림(PC 에서 다시)` : ''}`);
      setSent(true); setTitle(''); setDesc(''); setFiles([]);
    } catch (e: unknown) { toast(e instanceof Error ? e.message : '보내지 못했어요'); }
    finally { setBusy(false); setProg(''); }
  };
  return (
    <>
      {/* 순서 = 담당자 → 할 일 → 기한 → 업체(선택) → 자세히 — 필수 칸이 첫 화면에 (2026-10-12 검수) */}
      <Field title="담당자" need>
        {staff === null ? <div style={{ display: 'flex', gap: 8 }}>{[0, 1].map(i => <span key={i} style={{ width: 80, height: 48, borderRadius: 22, background: G.g100 }} />)}</div>
          : people.length === 0 ? <div style={{ fontSize: 16, color: G.g700 }}>담당자 목록을 못 불러왔어요 <button type="button" className="tds-btn" onClick={reloadStaff} style={{ minHeight: 44, border: 'none', background: 'none', color: NAVY, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>다시 불러오기</button></div>
            : <Chips multi items={people} value={to} onChange={setTo} />}
      </Field>
      <Field title="할 일" need><input value={title} onChange={e => setTitle(e.target.value)} placeholder="예: 재무제표 3개년 받아서 정리" style={input} /></Field>
      <Field title="기한"><DatePick value={due} onChange={setDue} /></Field>
      <Field title="업체"><Picker items={companies} value={cid} onChange={setCid} sub={compSub} placeholder="업체 이름으로 찾기" recent={byRecent} optional="업체 고르기 (없으면 비워 두세요)" /></Field>
      <Field title="자세히"><textarea value={desc} onChange={e => setDesc(e.target.value)} rows={4} placeholder="통화 내용 · 참고할 것" style={{ ...input, height: 'auto', minHeight: 110, padding: 14, lineHeight: 1.6, resize: 'vertical' }} /></Field>
      <Field title={`파일${files.length ? ` ${files.length}개` : ''}`}>
        {files.map((f, i) => (
          <div key={`${f.name}${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 52, padding: '4px 4px 4px 14px', marginBottom: 6, borderRadius: 14, background: TOSS.bg }}>
            <Icon name={/^image\//.test(f.type) ? 'img' : 'doc'} size={20} color={TOSS.sub} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 16, color: TOSS.text, ...clamp(1) }}>{f.name}</span>
            <span style={{ flexShrink: 0, width: 64, textAlign: 'right', fontSize: 15, color: TOSS.sub, fontVariantNumeric: 'tabular-nums' }}>{kb(f.size)}</span>
            <button type="button" className="tds-btn" aria-label={`${f.name} 빼기`} onClick={() => setFiles(fs => fs.filter((_, j) => j !== i))}
              style={{ flexShrink: 0, width: 44, height: 44, border: 'none', background: 'none', color: TOSS.sub, fontSize: 22, cursor: 'pointer' }}>×</button>
          </div>
        ))}
        {files.length < 10 && (
          <button type="button" className="tds-btn" onClick={() => fileRef.current && fileRef.current.click()}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', height: 52, borderRadius: 14, border: 'none', background: TOSS.blueLt, color: TOSS.blueDk, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            <Icon name="plus" size={18} color={TOSS.blueDk} sw={2.4} />{files.length ? '파일 더 넣기' : '파일 · 사진 넣기'}
          </button>
        )}
        <input ref={fileRef} type="file" multiple hidden accept="image/*,application/pdf,.pdf,.hwp,.hwpx,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt"
          onChange={e => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
      </Field>
      <SaveBar><Button h={56} muted={!!missing && !busy} onClick={() => void save()}>{busy ? (prog ? `파일 올리는 중 ${prog}` : '보내는 중') : missing || toBtn}</Button></SaveBar>
      {sent && <Next items={[{ t: '하나 더 요청', on: () => setSent(false) }, { t: '처음으로', on: () => go('home') }]} />}
    </>
  );
}
